import { PlusIcon } from "@heroicons/react/24/outline";
import { genEntityId, TaskId, TaskParentId } from "@sp-crm/core";
import { Icon } from "components/icon";
import { defaultLinkStyle } from "components/ui/link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import {
    GetEntitiesWithoutActiveTasksPaginatedQuery,
    GetUsersForRegionQuery,
    TaskBasicsFragment,
    UpdateTaskPayload,
    useCreateTaskMutation,
} from "generated/graphql";
import * as React from "react";
import { Link } from "react-router-dom";
import { useCurrentUserId } from "store/selectors/hooks";
import { getEntityIconName } from "util/entity-icons";
import { handleEvent } from "util/user-events";
import { TaskFormV2 } from "./task-form-v2";

interface EntityListItemProps {
    entity: GetEntitiesWithoutActiveTasksPaginatedQuery["getEntitiesWithoutActiveTasksPaginated"]["entities"][number];
    users: GetUsersForRegionQuery["getUsersForRegion"];
    onTaskCreated?: () => void;
}

export const EntityListItem: React.FC<EntityListItemProps> = ({
    entity,
    users,
    onTaskCreated,
}) => {
    const [showAddTask, setShowAddTask] = React.useState(false);
    const createTask = useCreateTaskMutation();
    const currentUserId = useCurrentUserId();

    const getEntityTypeIcon = (kind: string) => {
        const iconName = getEntityIconName(kind);
        return (
            <div className="w-4">
                <Icon name={iconName} />
            </div>
        );
    };

    const handleAddTask = () => {
        setShowAddTask(true);
    };

    const handleCancelAddTask = () => {
        setShowAddTask(false);
    };

    const handleTaskCreated = React.useCallback(
        async (parentId: TaskParentId, params: UpdateTaskPayload) => {
            await createTask.mutateAsync({
                parentId,
                params,
            });
            handleEvent("task-create");
            setShowAddTask(false);
            // Refresh the list after task creation
            onTaskCreated?.();
        },
        [createTask, onTaskCreated],
    );

    const initialTask = React.useMemo(() => {
        // Default new tasks to the current user
        // TODO: For clients, we should default to the client's assigned user when that data is available
        const defaultOwnerId = currentUserId || null;

        return {
            entity: {
                displayName: entity.displayName,
                path: entity.path,
                kind: entity.kind,
                contact: entity.contact,
            },
            task: {
                __typename: "Task" as const,
                done: false,
                id: genEntityId() as TaskId,
                parentId: entity.id as TaskParentId,
                status: "new",
                completedAt: null as TaskBasicsFragment["completedAt"],
                dueDate: null as TaskBasicsFragment["dueDate"],
                dueDateTime: null as TaskBasicsFragment["dueDateTime"],
                ownerId: defaultOwnerId,
                taskTypeId: null as TaskBasicsFragment["taskTypeId"],
                taskType: null as TaskBasicsFragment["taskType"],
                text: "",
                recurrencePattern: null as TaskBasicsFragment["recurrencePattern"],
                recurrenceInterval: null as TaskBasicsFragment["recurrenceInterval"],
                recurrenceDaysOfWeek: null as TaskBasicsFragment["recurrenceDaysOfWeek"],
                recurrenceDayOfMonth: null as TaskBasicsFragment["recurrenceDayOfMonth"],
            },
        };
    }, [entity, currentUserId]);

    return (
        <>
            <div className="md:space-x-1 lg:space-x-2 flex items-start lg:-mx-2 lg:px-2 py-4 hover:bg-brand-100">
                <div className="space-y-1 md:space-y-0 md:grid md:grid-cols-3 lg:grid-cols-5 md:gap-2 lg:gap-4 flex-1">
                    <div className="flex space-x-2 items-center row-span-2 lg:row-span-1 lg:col-span-2">
                        <div className="flex items-center space-x-2">
                            {getEntityTypeIcon(entity.kind)}
                            <Link
                                to={entity.path}
                                className={`${defaultLinkStyle} font-medium`}>
                                {entity.displayName}
                            </Link>
                        </div>
                    </div>
                    <div className="ml-8 md:ml-0">
                        {/* Space for contact/details if needed in future */}
                    </div>
                    <div className="ml-8 md:ml-0 text-sm text-gray-600">
                        {/* Space for due date equivalent */}
                    </div>
                    <div className="ml-8 md:ml-0 text-sm text-gray-600">
                        {/* Space for assigned user - entities don't have assigned users */}
                    </div>
                </div>
                <div className="flex items-start space-x-1">
                    <button
                        onClick={handleAddTask}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors">
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Task
                    </button>
                </div>
            </div>

            <Panel
                isOpen={showAddTask}
                onDismiss={handleCancelAddTask}
                type={PanelType.large}
                headerText="Add Task">
                <TaskFormV2
                    initial={initialTask}
                    mode="create"
                    onClose={handleCancelAddTask}
                    saveTask={handleTaskCreated}
                />
            </Panel>
        </>
    );
};
